// apps/dashboard/src/lib/should-use-redirect.ts
/**
 * signInWithPopup relies on the popup sharing storage with the opener via a hidden iframe on
 * the Firebase authDomain (cross-origin from dashboard.aksademia.com here). On an iOS "Add to
 * Home Screen" PWA (standalone display mode), other in-app/webview browsers (Facebook,
 * Instagram, TikTok, WhatsApp, ...), that popup either can't be opened as a real child window
 * or can't share storage back to the opener — signInWithPopup's promise then never resolves and
 * the user is silently left signed out, and the aks_session cookie never gets minted (see
 * docs/superpowers/specs/2026-07-18-session-cookie-auth-design.md §4.5). signInWithRedirect (a
 * full top-level navigation, completed via getRedirectResult on the next load) is the robust
 * fallback for those contexts.
 *
 * PLAIN mobile browsers (iOS Safari, Android Chrome) do NOT use redirect: because our authDomain
 * (auth.aksademia.com) is a different origin than the app (dashboard.aksademia.com), browser
 * storage partitioning (Chrome 115+) / ITP (Safari) drops signInWithRedirect's pending state, so
 * getRedirectResult() returns null on the return and the user is stranded on /login. Popup
 * completes cross-origin via postMessage regardless of storage partitioning (that's why desktop
 * works and why plain mobile signed in fine with popup pre-migration), so plain mobile uses popup.
 *
 * Canonical copy — apps/test-engine, apps/course-engine, apps/partner-portal keep matching
 * copies if/when they grow their own Google sign-in UI.
 */
const IN_APP_WEBVIEW_UA =
  /(FBAN|FBAV|Instagram|Line\/|Twitter|WhatsApp|WeChat|MicroMessenger|TikTok|musical_ly|Snapchat|Pinterest|GSA)/i;

export function shouldUseRedirect(): boolean {
  if (typeof window === 'undefined') return false;
  const nav = window.navigator as Navigator & { standalone?: boolean };

  // Standalone PWA (iOS home-screen / any display-mode:standalone) — popup can't
  // open a real child window here, so redirect is the only option.
  const isIosHomeScreenApp = nav.standalone === true;
  const isDisplayModeStandalone = window.matchMedia?.('(display-mode: standalone)').matches ?? false;
  if (isIosHomeScreenApp || isDisplayModeStandalone) return true;

  // Known in-app webviews (Instagram, WhatsApp, ...) — popup blocked; use redirect.
  const ua = nav.userAgent ?? '';
  if (IN_APP_WEBVIEW_UA.test(ua)) return true;

  // Everything else — including plain iOS Safari / Android Chrome — uses popup.
  // signInWithRedirect is broken for plain mobile here: getRedirectResult()
  // returns null after the round-trip because storage partitioning / ITP drops
  // the pending-redirect state (authDomain != app origin), stranding the user on
  // /login. Popup completes cross-origin via postMessage and is unaffected.
  return false;
}
